<template>
  <div class="settings-wrapper">
    <div class="content-area">
      <div class="page-header">
        <div class="page-header__lead">
          <h1 class="page-title">
            Settings
          </h1>
          <p class="page-subtitle">
            Configure your application preferences and system settings.
          </p>
        </div>
      </div>

      <div class="main-content">
        <div class="settings-grid">
          <!-- General Settings -->
          <v-card class="settings-card" outlined>
            <div class="card-inner">
              <h2 class="settings-card-title">
                General Settings
              </h2>
              <div class="field-group">
                <label class="field-label">Company Name</label>
                <v-text-field
                  v-model="form.companyName"
                  outlined
                  dense
                  hide-details
                  class="settings-field"/>
              </div>
              <div class="field-group">
                <label class="field-label">Support Email</label>
                <v-text-field
                  v-model="form.supportEmail"
                  outlined
                  dense
                  hide-details
                  type="email"
                  class="settings-field"/>
              </div>
            </div>
          </v-card>

          <!-- API Settings -->
          <v-card class="settings-card" outlined>
            <div class="card-inner">
              <h2 class="settings-card-title">
                API Settings
              </h2>
              <div class="field-group">
                <label class="field-label">API Timeout (seconds)</label>
                <v-text-field
                  v-model="form.apiTimeout"
                  outlined
                  dense
                  hide-details
                  type="number"
                  class="settings-field"/>
              </div>
              <div class="field-group">
                <label class="field-label">Max Concurrent Calls</label>
                <v-text-field
                  v-model="form.maxConcurrentCalls"
                  outlined
                  dense
                  hide-details
                  type="number"
                  class="settings-field"/>
              </div>
              <div class="field-group">
                <label class="field-label">Default Voice Model</label>
                <v-select
                  v-model="form.defaultVoiceModel"
                  :items="voiceModelOptions"
                  outlined
                  dense
                  hide-details
                  class="settings-field"/>
              </div>
            </div>
          </v-card>

          <!-- Security & Privacy -->
          <v-card class="settings-card" outlined>
            <div class="card-inner">
              <h2 class="settings-card-title">
                Security & Privacy
              </h2>
              <div class="checkbox-group">
                <v-checkbox
                  v-model="form.enableCallRecording"
                  label="Enable Call Recording"
                  color="primary"
                  hide-details
                  class="settings-checkbox"/>
              </div>
              <div class="checkbox-group">
                <v-checkbox
                  v-model="form.enableAuditLogs"
                  label="Enable Audit Logs"
                  color="primary"
                  hide-details
                  class="settings-checkbox"/>
              </div>
              <div class="field-group">
                <label class="field-label">Session Timeout (minutes)</label>
                <v-text-field
                  v-model="form.sessionTimeout"
                  outlined
                  dense
                  hide-details
                  type="number"
                  class="settings-field"/>
              </div>
            </div>
          </v-card>

          <!-- Notifications -->
          <v-card class="settings-card" outlined>
            <div class="card-inner">
              <h2 class="settings-card-title">
                Notifications
              </h2>
              <div class="checkbox-group">
                <v-checkbox
                  v-model="form.emailNotifications"
                  label="Email Notifications"
                  color="primary"
                  hide-details
                  class="settings-checkbox"/>
              </div>
              <div class="checkbox-group">
                <v-checkbox
                  v-model="form.smsNotifications"
                  label="SMS Notifications"
                  color="primary"
                  hide-details
                  class="settings-checkbox"/>
              </div>
              <div class="checkbox-group">
                <v-checkbox
                  v-model="form.webhookNotifications"
                  label="Webhook Notifications"
                  color="primary"
                  hide-details
                  class="settings-checkbox"/>
              </div>
            </div>
          </v-card>

          <!-- Advanced -->
          <v-card class="settings-card" outlined>
            <div class="card-inner">
              <h2 class="settings-card-title">
                Advanced
              </h2>
              <div class="checkbox-group">
                <v-checkbox
                  v-model="form.debugMode"
                  label="Debug Mode"
                  color="primary"
                  hide-details
                  class="settings-checkbox"/>
              </div>
              <div class="checkbox-group">
                <v-checkbox
                  v-model="form.enableAnalytics"
                  label="Enable Analytics"
                  color="primary"
                  hide-details
                  class="settings-checkbox"/>
              </div>
              <div class="field-group">
                <label class="field-label">Data Retention (days)</label>
                <v-text-field
                  v-model="form.dataRetentionDays"
                  outlined
                  dense
                  hide-details
                  type="number"
                  class="settings-field"/>
              </div>
            </div>
          </v-card>

          <!-- Account -->
          <v-card class="settings-card" outlined>
            <div class="card-inner">
              <h2 class="settings-card-title">
                Account
              </h2>
              <p class="account-desc">
                Sign out of your account. You will need to sign in again to access the application.
              </p>
              <v-btn
                color="error"
                outlined
                elevation="0"
                class="logout-btn"
                @click="openLogoutDialog">
                <v-icon left size="20">
                  mdi-logout
                </v-icon>
                Logout
              </v-btn>
            </div>
          </v-card>
        </div>

        <div class="actions-row">
          <v-btn color="primary" elevation="0" @click="saveSettings">
            Save Changes
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Logout confirmation dialog -->
    <v-dialog
      v-model="showLogoutDialog"
      max-width="400"
      persistent
      content-class="logout-dialog">
      <v-card>
        <v-card-title class="logout-dialog-title">
          Log out
        </v-card-title>
        <v-card-text class="logout-dialog-text">
          Are you sure you want to log out?
        </v-card-text>
        <v-card-actions class="logout-dialog-actions">
          <v-spacer/>
          <v-btn text @click="cancelLogout">
            Cancel
          </v-btn>
          <v-btn color="error" text @click="confirmLogout">
            Log out
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'Settings',
  data() {
    return {
      showLogoutDialog: false,
      voiceModelOptions: [
        'Professional Female',
        'Professional Male',
        'Friendly Female',
        'Friendly Male',
        'Neutral',
      ],
      form: {
        companyName: 'Mank Technology',
        supportEmail: 'support@mank.tech',
        apiTimeout: '30',
        maxConcurrentCalls: '10',
        defaultVoiceModel: 'Professional Female',
        enableCallRecording: true,
        enableAuditLogs: true,
        sessionTimeout: '60',
        emailNotifications: true,
        smsNotifications: false,
        webhookNotifications: true,
        debugMode: false,
        enableAnalytics: true,
        dataRetentionDays: '90',
      },
    };
  },
  methods: {
    saveSettings() {
      this.$store.dispatch('ui/showNotification', {
        type: 'success',
        message: 'Settings saved successfully.',
        timeout: 3000,
      });
    },
    openLogoutDialog() {
      this.showLogoutDialog = true;
    },
    cancelLogout() {
      this.showLogoutDialog = false;
    },
    async confirmLogout() {
      this.showLogoutDialog = false;
      await this.$store.dispatch('auth/logout');
      this.$router.push('/login');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';
@import '../styles/colors.scss';

.page-subtitle {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  font-size: $body-3-size;
  line-height: 1.5em;
  color: $light-foreground-4;
  margin: 0;
}

.main-content {
  padding: 24px;
  gap: 24px;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.settings-card {
  border-radius: 12px;
  border: 1px solid $light-border;
  box-shadow: none;

  .card-inner {
    padding: 20px;
  }
}

.settings-card-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: $body-2-size;
  line-height: 1.4em;
  color: $light-foreground-1;
  margin: 0 0 16px 0;
}

.field-group {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.field-label {
  display: block;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 500;
  font-size: $body-3-size;
  line-height: 1.4em;
  color: $light-foreground-1;
  margin-bottom: 8px;
}

.checkbox-group {
  margin-bottom: 8px;

  &:last-of-type {
    margin-bottom: 16px;
  }
}

.settings-checkbox {
  margin: 0;
}

.settings-field {
  margin: 0;
}

.account-desc {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: $body-3-size;
  line-height: 1.5;
  color: $light-foreground-4;
  margin: 0 0 16px 0;
}

.logout-btn {
  text-transform: none;
}

.logout-dialog-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  font-size: $text-size-18;
}

.logout-dialog-text {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: $body-3-size;
  color: $light-foreground-4;
}

.logout-dialog-actions {
  padding: 8px 16px 16px;
}

.actions-row {
  display: flex;
  justify-content: flex-start;
  padding-top: 8px;
}

@media (max-width: 960px) {
  .settings-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .settings-grid {
    grid-template-columns: 1fr;
  }
}
</style>
